import { Component, OnInit, ChangeDetectionStrategy, OnDestroy } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import {
  EmailUpdateDialogStates,
  IEmailUpdate,
  MAX_LENGTHS,
  ErrorMessages
} from '../../../../../interfaces/user-profile';
import { RxState } from '@rx-angular/state';
import { UserProfileFacadeService } from '../../../../../services/facades/user-profile-facade.service';
import { filter, take, map, Observable, of} from 'rxjs';
import { EmailValidator } from '../../../../../validators/email-validator';
import { RequiredTrimmedValidator } from '../../../../../validators/required-trimmed.validator';

export interface IPersonalDetailsEmailUpdateDialogState extends IEmailUpdate {
  CurrentDialogState: EmailUpdateDialogStates;
  ResendTimer: number; // in seconds
}

export const initialState: IPersonalDetailsEmailUpdateDialogState = {
  RequestId: null,
  CodeInfo: null,
  RequestUpdate: null,
  CodeVerification: null,
  GenerateCode: null,
  CurrentDialogState: EmailUpdateDialogStates.UpdateEmail,
  ResendTimer: 0,
};

@Component({
  selector: 'personal-details-email-update-dialog',
  templateUrl: './personal-details-email-update-dialog.component.html',
  styleUrls: ['./personal-details-email-update-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [RxState],
  standalone: false,
})
export class PersonalDetailsEmailUpdateDialogComponent implements OnInit, OnDestroy {
  readonly state$ = this.state.select();
  readonly MAX_EMAIL_LENGTH = MAX_LENGTHS.email;

  emailChangeForm: FormGroup<{
    email: FormControl<string | null>;
  }>;
  verificationCodeForm: FormGroup<{
    code: FormControl<string | null>;
  }>;
  formSubmitAttempt: boolean;
  EmailUpdateDialogStates = EmailUpdateDialogStates;

  errors = ErrorMessages;

  timerInterval: NodeJS.Timeout;

  get MODAL_TEXTS() {
    return {
      [EmailUpdateDialogStates.UpdateEmail]: {
        header: 'Change your email',
        description:
          'Email is used as a login to the My7N Platform. Without access to it you won\'t be able to use 7N services and benefits.',
        confirmBtnText: 'Accept',
        cancelBtnText: 'Cancel',
      },
      [EmailUpdateDialogStates.VerifyCode]: {
        header: 'Almost there - check your inbox',
        description: `To change your email, enter the code ${this.state.get('CodeInfo', 'CodeNumber')} from ${this.state.get('CodeInfo', 'CodeDate')} we just sent you to ${this.emailChangeForm?.get('email')?.value}.`,
        confirmBtnText: 'Confirm',
        cancelBtnText: 'Cancel',
      },
    };
  }

  constructor(
    public dialogRef: MatDialogRef<PersonalDetailsEmailUpdateDialogComponent>,
    private userProfileFacadeService: UserProfileFacadeService,
    private state: RxState<IPersonalDetailsEmailUpdateDialogState>
  ) {
    this.state.set({ ...initialState });
  }

  ngOnInit() {
    this.initLocalStateConnections();

    this.emailChangeForm = new FormGroup({
      email: new FormControl('', [RequiredTrimmedValidator, Validators.maxLength(this.MAX_EMAIL_LENGTH), EmailValidator]),
    });

    this.verificationCodeForm = new FormGroup({
      code: new FormControl('', [RequiredTrimmedValidator, Validators.pattern(/^[0-9]{7}$/)]),
    });
  }

  confirmAction() {
    if (this.state.get('CurrentDialogState') === EmailUpdateDialogStates.UpdateEmail) {
      this.emailChangeForm.markAllAsTouched();

      if (this.emailChangeForm.invalid) {
        console.debug('[PersonalDetailsEmailUpdateDialog] Email form is invalid');
        return;
      }

      this.userProfileFacadeService.requestEmailUpdate(this.emailChangeForm.value.email);

      this.backendValidation(this.emailChangeForm.get('email'), 'RequestUpdate').subscribe((validationErrors) => {
        this.emailChangeForm.get('email').setErrors(validationErrors);

        if (validationErrors === null) {
          this.state.set({ CurrentDialogState: EmailUpdateDialogStates.VerifyCode });
          this.verificationCodeForm.reset();
        }
      });
    } else if (this.state.get('CurrentDialogState') === EmailUpdateDialogStates.VerifyCode) {
      this.verificationCodeForm.markAllAsTouched();

      if (this.verificationCodeForm.invalid) {
        console.debug('[PersonalDetailsEmailUpdateDialog] Verification code form is invalid');
        return;
      }

      this.userProfileFacadeService.emailUpdateCodeVerify(this.verificationCodeForm.value.code);

      this.backendValidation(this.verificationCodeForm.get('code'), 'CodeVerification').subscribe(
        (validationErrors) => {
          this.verificationCodeForm.get('code').setErrors(validationErrors);

          if (validationErrors === null) {
            this.dialogRef.close(true);
          }
        }
      );
    }
  }

  resendCode() {
    this.userProfileFacadeService.emailUpdateGenerateCode();

    this.state
      .select('GenerateCode')
      .pipe(
        filter((generateCodeState) => generateCodeState !== null && generateCodeState.Loading === false && generateCodeState.Error === false),
        take(1)
      )
      .subscribe(() => {
        this.state.set({ ResendTimer: 30 });

        // Countdown logic for resend timer
        this.timerInterval = setInterval(() => {
          const currentTimer = this.state.get('ResendTimer');
          if (currentTimer > 0) {
            this.state.set({ ResendTimer: currentTimer - 1 });
          } else {
            clearInterval(this.timerInterval);
          }
        }, 1000);
      });
  }

  closeDialog() {
    this.dialogRef.close(false);
  }

  initLocalStateConnections() {
    this.state.connect('RequestId', this.userProfileFacadeService.emailUpdateRequestId$);
    this.state.connect('CodeInfo', this.userProfileFacadeService.emailUpdateCodeInfo$);
    this.state.connect('RequestUpdate', this.userProfileFacadeService.emailUpdateRequestUpdate$);
    this.state.connect('CodeVerification', this.userProfileFacadeService.emailUpdateCodeVerification$);
    this.state.connect('GenerateCode', this.userProfileFacadeService.emailUpdateGenerateCode$);
  }

  /**
   * Async validator that listens to RequestUpdate status changes
   * and adds validation errors based on the status as the error key
   */
  private backendValidation(
    control: AbstractControl,
    stateStatusChange: 'RequestUpdate' | 'CodeVerification'
  ): Observable<ValidationErrors | null> {
    if (!control.value) {
      return of(null);
    }

    // Listen to RequestUpdate status changes from state
    return this.state.select(stateStatusChange).pipe(
      filter((statusChange) => statusChange !== null && statusChange.Loading === false),
      take(1),
      map((statusChange) => {
        const status = statusChange.Status;

        // Return error with status as key if there's an error status
        if (statusChange.Error) {
          return {
            [status]: true,
          };
        }

        return null;
      })
    );
  }

  ngOnDestroy() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
  }
}
